import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, playersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  Users, 
  CalendarCheck, 
  Heart, 
  ChevronRight,
  AlertCircle,
  Scale,
  LogIn
} from 'lucide-react';
import { formatDateTime, getStatusColor } from '../lib/utils';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [players, setPlayers] = useState([]);
  const [positionModal, setPositionModal] = useState({ open: false, position: null, players: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, playersRes] = await Promise.all([
        dashboardAPI.getStats(),
        playersAPI.getAll()
      ]);
      setStats(statsRes.data);
      setPlayers(playersRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-[#71717A]">Loading...</div>
        </div>
      </Layout>
    );
  }

  const attendanceRate = stats?.attendance_today?.rate || 0;

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="overline">Overview</p>
            <h1 className="font-display text-4xl lg:text-5xl text-white mt-2">DASHBOARD</h1>
          </div>
          
          {/* Check-In Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={() => window.open('/coach-checkin', '_blank')}
              className="bg-[#FFD60A] hover:bg-[#FFD60A]/90 text-black font-bold text-lg h-14 px-6 btn-press"
              data-testid="open-coach-checkin-btn"
            >
              <Users className="w-6 h-6 mr-2" />
              COACH CHECK-IN
            </Button>
            <Button
              onClick={() => window.open('/kiosk', '_blank')}
              className="bg-[#32D74B] hover:bg-[#28a83d] text-black font-bold text-lg h-14 px-6 btn-press"
              data-testid="open-kiosk-btn"
            >
              <LogIn className="w-6 h-6 mr-2" />
              ATHLETE CHECK-IN
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Players */}
          <Card className="bg-[#121212] border-[#27272A] card-hover" data-testid="stat-total-players">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="overline mb-2">Total Players</p>
                  <p className="font-display text-4xl text-white">{stats?.total_players || 0}</p>
                </div>
                <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#007AFF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Today's Attendance */}
          <Card className="bg-[#121212] border-[#27272A] card-hover" data-testid="stat-attendance">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="overline mb-2">Attendance</p>
                  <p className={`font-display text-4xl ${attendanceRate >= 80 ? 'text-[#32D74B]' : attendanceRate >= 50 ? 'text-[#FFD60A]' : 'text-[#FF3B30]'}`}>
                    {attendanceRate}%
                  </p>
                  <p className="text-xs text-[#71717A] mt-1">
                    {stats?.attendance_today?.present || 0}/{stats?.total_players || 0} present
                  </p>
                </div>
                <div className="w-10 h-10 bg-[#32D74B]/10 flex items-center justify-center">
                  <CalendarCheck className="w-5 h-5 text-[#32D74B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Injuries */}
          <Card className="bg-[#121212] border-[#27272A] card-hover" data-testid="stat-injuries">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="overline mb-2">Active Injuries</p>
                  <p className={`font-display text-4xl ${stats?.active_injuries > 0 ? 'text-[#FF3B30]' : 'text-[#32D74B]'}`}>
                    {stats?.active_injuries || 0}
                  </p>
                </div>
                <div className="w-10 h-10 bg-[#FF3B30]/10 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-[#FF3B30]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weight Logs Today */}
          <Card className="bg-[#121212] border-[#27272A] card-hover" data-testid="stat-weight">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="overline mb-2">Weight Logged</p>
                  <p className="font-display text-4xl text-white">
                    {stats?.weight_logs_today || 0}
                  </p>
                  <p className="text-xs text-[#71717A] mt-1">today</p>
                </div>
                <div className="w-10 h-10 bg-[#FFD60A]/10 flex items-center justify-center">
                  <Scale className="w-5 h-5 text-[#FFD60A]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts & Lists Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Daily Average & Weight by Position */}
          <Card className="lg:col-span-2 bg-[#121212] border-[#27272A]" data-testid="weight-stats">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <Scale className="w-5 h-5 text-[#007AFF]" />
                Team Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily Attendance - End of Day */}
                <div className="p-6 bg-[#1A1A1A] border border-[#27272A]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-2">Today's Attendance</p>
                  <p className="font-display text-5xl text-white">
                    {stats?.attendance_today?.present || 0}
                  </p>
                  <p className="text-sm text-[#71717A] mt-1">
                    of {stats?.total_players || 0} players present
                  </p>
                  <p className="text-xs text-[#71717A] mt-2">
                    Daily Avg: {stats?.daily_avg_attendance || 0} players/day
                  </p>
                </div>
                
                {/* Average Weight by Position - Clickable */}
                <div>
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-3">Avg Weight by Position</p>
                  <div className="grid grid-cols-2 gap-1">
                    {stats?.avg_weight_by_position?.map((pos) => (
                      <button 
                        key={pos.position} 
                        onClick={() => setPositionModal({ open: true, position: pos.position, players: pos.players })}
                        className="flex items-center gap-2 px-2 py-1.5 bg-[#1A1A1A] border border-[#27272A] hover:border-[#FF3B30] transition-colors text-left"
                      >
                        <span className="text-[#FF3B30] font-bold">{pos.initials}</span>
                        <span className="text-white font-medium">{pos.avg_weight || '-'}</span>
                      </button>
                    )) || (
                      <p className="text-[#71717A] col-span-2 text-center py-4">No weight data</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Position Players Modal */}
          <Dialog open={positionModal.open} onOpenChange={(open) => setPositionModal({ ...positionModal, open })}>
            <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-sm">
              <DialogHeader>
                <DialogTitle>{positionModal.position}</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {positionModal.players?.map((player, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-[#1A1A1A] border border-[#27272A]">
                    <span className="text-white">{player.name}</span>
                    <span className="text-[#71717A]">{player.weight ? `${player.weight} lbs` : '-'}</span>
                  </div>
                ))}
                {(!positionModal.players || positionModal.players.length === 0) && (
                  <p className="text-[#71717A] text-center py-4">No players</p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Recent Alerts */}
          <Card className="bg-[#121212] border-[#27272A]" data-testid="recent-alerts">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center justify-between text-white">
                <span className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-[#FFD60A]" />
                  Recent Alerts
                </span>
                <Link to="/alerts" className="text-xs text-[#007AFF] hover:underline">
                  View All
                </Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {stats?.recent_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {stats.recent_alerts.map((alert) => (
                    <div 
                      key={alert.id} 
                      className="p-3 bg-[#1A1A1A] border border-[#27272A]"
                    >
                      <p className="text-sm text-white line-clamp-2">{alert.message}</p>
                      <p className="text-xs text-[#71717A] mt-2">
                        {formatDateTime(alert.sent_at)}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#71717A] py-8">No recent alerts</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Player Status Overview */}
        <Card className="bg-[#121212] border-[#27272A]" data-testid="player-status">
          <CardHeader className="border-b border-[#27272A]">
            <CardTitle className="flex items-center justify-between text-white">
              <span>Player Status Overview</span>
              <Link to="/roster" className="text-xs text-[#007AFF] hover:underline flex items-center">
                View Roster <ChevronRight className="w-4 h-4" />
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th>Position</th>
                    <th>Status</th>
                    <th>Weight</th>
                  </tr>
                </thead>
                <tbody>
                  {players.slice(0, 5).map((player) => (
                    <tr key={player.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#27272A] flex items-center justify-center text-white font-bold text-sm">
                            #{player.jersey_number}
                          </div>
                          <span className="text-white font-medium">{player.name}</span>
                        </div>
                      </td>
                      <td className="text-[#A1A1AA]">{player.position}</td>
                      <td>
                        <span className={`text-sm font-medium capitalize ${getStatusColor(player.status)}`}>
                          {player.status}
                        </span>
                      </td>
                      <td className="text-[#A1A1AA]">
                        {player.current_weight ? `${player.current_weight} lbs` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {players.length === 0 && (
                <div className="text-center py-12 text-[#71717A]">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No players added yet</p>
                  <Link to="/roster" className="text-[#007AFF] hover:underline text-sm">
                    Add your first player
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
